import { computed, reactive } from 'vue';

export type SlateUserToken = {
  'token': string;

  'contact_name': string;
  'contact_email': string;
  'contact_slug': string;
  'contact_id': string;
  'homepage': string;

  'exp': string;
  'remote_ip': string;

  'settings': Record<string, any>;

  'is_staff': boolean;
  'is_admin': boolean;
};

export type SlateImpersonationDetails = {
  'impersonator_id': string;
  'impersonator_name': string;
  'impersonator_email': string;
  'target_id': string;
  'target_name': string;
  'target_email': string;
  'started': string;
};

type SlateStoredImpersonation = {
  'restoreToken': string;
  'impersonation': SlateImpersonationDetails;
};

export class SlateAuthService {
  is_logged_in = false;
  login_error: string | null = null;

  permissions: Record<string, boolean> = {};
  user_details: SlateUserToken | null = null;
  impersonation: SlateImpersonationDetails | null = null;
  groups: string[] = [];
  private baseURL: string = `${import.meta.env.VITE_API_URL}auth/`;
  private impersonationStorageKey = 'slate-auth-impersonation';

  get token(): string | null {
    return localStorage.getItem('slate-auth-token');
  }
  set token(tokenVal: string | null) {
    if (typeof tokenVal === 'string') {
      localStorage.setItem('slate-auth-token', tokenVal);
      return;
    }
    localStorage.removeItem('slate-auth-token');
  }

  private clear() {
    // is_logged_in is set here first to avoid race conditions
    this.is_logged_in = false;
    this.login_error = null;

    this.token = null;
    this.permissions = {};
    this.user_details = null;
    this.groups = [];
    this.impersonation = null;
    localStorage.removeItem(this.impersonationStorageKey);
  }

  private setLoginDetails(parsed_results: any) {
    this.user_details = parsed_results.payload as SlateUserToken;
    this.token = this.user_details.token;
    this.login_error = null;
    this.permissions = parsed_results.permissions;
    this.groups = parsed_results.groups;
    this.impersonation = parsed_results.impersonation;

    if (!this.impersonation) {
      localStorage.removeItem(this.impersonationStorageKey);
    }

    // is_logged_in is set here last to avoid race conditions
    this.is_logged_in = true;
  }

  private getStoredImpersonation(): SlateStoredImpersonation | null {
    const stored = localStorage.getItem(this.impersonationStorageKey);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored) as SlateStoredImpersonation;
    } catch {
      localStorage.removeItem(this.impersonationStorageKey);
      return null;
    }
  }

  async login(username: string, password: string, options: Record<string, any> = {}): Promise<SlateUserToken | null> {
    const url = `${this.baseURL}get-token/`;
    this.login_error = null;

    const payload: Record<string, any> = {
      'username': username,
      'password': password,
    };

    if (Object.keys(options).includes('remember')) {
      payload.remember = options.remember;
    }
    if (Object.keys(options).includes('login_type')) {
      payload.login_type = options.login_type;
    }

    const results: Response = await fetch(url, {
      'method': 'POST',
      'credentials': 'same-origin',
      'cache': 'no-cache',
      'headers': {
        'Content-Type': 'application/json',
      },
      'body': JSON.stringify(payload),
    });

    const parsed_results = await results.json();

    if (results.status >= 401) {
      this.login_error = parsed_results.error;
      throw new Error(parsed_results.error);
    }

    // The settings property doesn't exist on the response.
    // parsed_results.settings = JSON.parse(parsed_results.settings);
    this.setLoginDetails(parsed_results);

    return this.user_details;
  }

  // track allow components to track logouts
  // without generating a watcher
  private logout_hooks: Array<() => Promise<void>> = [];
  logoutHook(logout_hook: () => Promise<void>): void {
    this.logout_hooks.push(logout_hook);
  }
  private async runLogoutHooks(): Promise<void> {
    // using for.. in.. to avoid creating another context for async/await
    for (const idx in this.logout_hooks) {
      if (typeof this.logout_hooks[idx] === 'undefined') continue;
      await this.logout_hooks[idx]();
    }
  }

  async logout(): Promise<void> {
    const url = `${this.baseURL}destroy-token/`;

    await fetch(url, {
      'method': 'POST',
      'credentials': 'same-origin',
      'cache': 'no-cache',
      'headers': {
        'Content-Type': 'application/json',
      },
    });

    this.clear();

    // run logout hooks
    await this.runLogoutHooks();
  }

  async checkToken(): Promise<SlateUserToken | null> {
    const url = `${this.baseURL}check-token/`;

    const fetchOptions: RequestInit = {
      'method': 'POST',
      'credentials': 'same-origin',
      'cache': 'no-cache',
      'headers': {
        'Content-Type': 'application/json',
      },
    };

    const result: void | Response = await fetch(url, fetchOptions).catch((e) => {});

    if (!result) {
      return null;
    }

    if (result.status === 401) {
      this.clear();
      return null;
    }

    const parsed_result = await result.json();
    this.setLoginDetails(parsed_result);

    return parsed_result;
  }

  async impersonate(contactId: string): Promise<SlateUserToken | null> {
    if (!this.token || !this.user_details) {
      throw new Error('Not logged in');
    }
    if (this.impersonation) {
      throw new Error('Already impersonating a user');
    }

    const url = `${this.baseURL}impersonate/`;
    const restoreToken = this.token;

    const results = await fetch(url, {
      'method': 'POST',
      'credentials': 'same-origin',
      'cache': 'no-cache',
      'headers': {
        'Content-Type': 'application/json',
        'Authorization': this.token,
      },
      'body': JSON.stringify({ 'contact': contactId }),
    });

    const parsed_results = await results.json();

    if (results.status >= 400) {
      throw new Error(parsed_results.error ?? 'Unable to impersonate user');
    }

    localStorage.setItem(
      this.impersonationStorageKey,
      JSON.stringify({
        restoreToken,
        'impersonation': parsed_results.impersonation,
      }),
    );

    this.setLoginDetails(parsed_results);

    return this.user_details;
  }

  async stopImpersonating(): Promise<SlateUserToken | null> {
    const storedImpersonation = this.getStoredImpersonation();
    if (!storedImpersonation) {
      return this.user_details;
    }
    if (!this.token) {
      this.clear();
      return null;
    }

    const url = `${this.baseURL}stop-impersonating/`;

    const results = await fetch(url, {
      'method': 'POST',
      'credentials': 'same-origin',
      'cache': 'no-cache',
      'headers': {
        'Content-Type': 'application/json',
        'Authorization': this.token,
      },
      'body': JSON.stringify({ 'restore_token': storedImpersonation.restoreToken }),
    });

    const parsed_results = await results.json();

    if (results.status >= 400) {
      throw new Error(parsed_results.error ?? 'Unable to stop impersonating user');
    }

    this.setLoginDetails(parsed_results);

    return this.user_details;
  }
}

export const SlateAuth = reactive(new SlateAuthService());
export function useSlateAuth() {
  return SlateAuth;
}

export const CanDeleteAnyObject = computed(() => {
  return SlateAuth.user_details?.is_admin && SlateAuth.permissions.can_delete_any_object;
});
